import React, { RefCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import GenericDateRange from "@/components/dateTime/genericDateRange";
import { DEFAULTPICKERDATE } from "@/types/Entities";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import PickerFormControlLabel from "@/components/tables/fields/PickerFormControlLabel";
import {
  dateNotTodayRule,
  requiredRule,
} from "@/components/tables/fields/fieldRules";
import { textFieldSx } from "@/components/tables/fields/fieldStyles";
import { useHideDateFieldForFauxOverdue } from "@/components/tables/fields/useHideDateFieldForFauxOverdue";
import {
  applyPickedDate,
  DATE_FORM_FORMAT,
  toDayjsOrNull,
} from "@/components/tables/fields/crudDateValues";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudDateFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "validate"
    | "errorMessageValidation"
    | "defaultDate"
  >;
  entityOfTable?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: string) => void;
}

const CrudDateField = ({
  field,
  entityOfTable,
  validationTargetRef,
  onChange,
}: CrudDateFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control, setValue, trigger } = useFormContext();
  const hidden = useHideDateFieldForFauxOverdue(entityOfTable, field.id);
  const formValue = useWatch({ control, name });
  const [mountTime] = useState(() => dayjs());
  // shown date and form value are kept apart on purpose: a default "today" is only displayed, never submitted (preserved quirk)
  const [displayedDate, setDisplayedDate] = useState<Dayjs | null>(
    () =>
      toDayjsOrNull(formValue) ??
      (field.defaultDate === DEFAULTPICKERDATE.TODAY ? dayjs() : null)
  );

  useEffect(() => {
    if (formValue && !dayjs.isDayjs(formValue)) {
      setDisplayedDate(toDayjsOrNull(formValue)); // a dayjs object only ends up in the form after unparsable input; the picker then keeps what was typed
    }
  }, [formValue]);

  if (hidden) return null;

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={{
        ...dateNotTodayRule(field, t),
        ...requiredRule(field.required, field.errorMessage),
      }}
      validationTargetRef={validationTargetRef}
      render={({ field: { onChange: setFormValue, ...rest }, error }) => (
        <PickerFormControlLabel header={field.header} required={field.required}>
          <GenericDateRange
            selectionMode="single"
            value={displayedDate}
            onChange={(picked) => {
              if (!picked || !picked.isValid()) {
                setFormValue(mountTime); 
                return;
              }
              const updated = applyPickedDate(displayedDate, picked);
              setDisplayedDate(updated);
              const formatted = updated.format(DATE_FORM_FORMAT);
              setFormValue(formatted);
              onChange?.(formatted);
            }}
            onClear={() => {
              setDisplayedDate(null);
              setValue(name, "");
              trigger(name);
            }}
            textFieldProps={{
              name,
              onBlur: rest.onBlur,
              inputRef: rest.ref,
              error: !!error,
              sx: textFieldSx,
            }}
            className={styles.datePicker}
          />
        </PickerFormControlLabel>
      )}
    />
  );
};

export default CrudDateField;
